import { useState, useEffect } from 'react';

interface UseDebouncedValueReturnValue<T> {
    value: T;
    debouncedValue: T;
    onValueChange: (value: T) => void;
}

interface State<T> {
    value: T;
    debounced: T;
}

const useDebouncedValue = <T = unknown>(
    initialValue?: T
): UseDebouncedValueReturnValue<T> => {
    const [state, setState] = useState<State<T>>({
        value: initialValue,
        debounced: initialValue,
    });

    const handleValueChange = (value: T): void => {
        setState((state) => ({ ...state, value }));
    };

    useEffect((): VoidFunction => {
        const timeout = setTimeout(
            () =>
                setState((state) => ({
                    ...state,
                    debounced: state.value,
                })),
            500
        );

        return () => clearTimeout(timeout);
    }, [state.value]);

    return {
        value: state.value,
        debouncedValue: state.debounced,
        onValueChange: handleValueChange,
    };
};

export default useDebouncedValue;
